import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, TrendingUp } from "lucide-react";

const TYPES = [
  { value: "", label: "All" },
  { value: "solar", label: "Solar" },
  { value: "street_light", label: "Street Light" },
  { value: "biogas", label: "Biogas" },
  { value: "cbg", label: "CBG" },
  { value: "waste_energy", label: "Waste-to-Energy" },
  { value: "hydro", label: "Hydro" },
  { value: "ev", label: "EV" },
];

function StatusPill({ s }) {
  const map = {
    completed: "badge-status-open",
    in_progress: "badge-status-approved",
    planning: "badge-status-pending",
    on_hold: "badge-status-critical",
  };
  return <span className={`${map[s] || "badge-status-closed"} capitalize`}>{s.replace("_"," ")}</span>;
}

export default function ProjectsPage() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = sp.get("type") || "";

  useEffect(() => {
    setLoading(true);
    const q = t ? `?project_type=${t}` : "";
    api.get(`/projects${q}`).then(({ data }) => setItems(data.items)).finally(() => setLoading(false));
  }, [t]);

  return (
    <div className="container-x py-12">
      <div className="label-tiny text-[hsl(var(--primary))]">Projects Portfolio</div>
      <h1 className="mt-1 text-3xl lg:text-5xl font-heading font-bold">Punjab's renewable buildout — live.</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">Search {items.length} active projects across all 22 districts.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {TYPES.map((x) => (
          <button
            key={x.value}
            onClick={() => setSp(x.value ? { type: x.value } : {})}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${t === x.value ? "bg-[hsl(var(--primary))] text-white border-transparent" : "border-border hover:bg-muted"}`}
            data-testid={`filter-${x.value || "all"}`}
          >
            {x.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({length: 6}).map((_,i)=>(
            <div key={i} className="rounded-xl border border-border p-5 animate-pulse h-40"/>
          ))}
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="rounded-xl border border-border bg-card p-5 hover:-translate-y-0.5 transition-transform">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="label-tiny text-muted-foreground">{p.project_type.replace("_"," ")}</div>
                  <div className="mt-1 font-heading font-semibold">{p.name}</div>
                </div>
                <StatusPill s={p.status}/>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-muted-foreground">District</div>
                  <div className="font-medium flex items-center gap-1"><MapPin className="h-3 w-3"/>{p.district}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Capacity</div>
                  <div className="font-medium">{p.capacity} MW/units</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Budget</div>
                  <div className="font-medium">₹{(p.budget/1e5).toFixed(1)}L</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">{p.progress}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${p.progress}%` }}/>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
